import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	type PinoLogEntry,
	getActivities,
	getActivityDetails,
	getActivityLogs,
	getActivitySummary,
} from "@/services/api";
import type { Activity } from "@efes/shared";
import { useQuery } from "@tanstack/react-query";
import {
	AlertTriangle,
	ArrowDownUp,
	CheckCircle2,
	ChevronDown,
	ChevronRight,
	ClipboardCheck,
	KeyRound,
	Loader2,
	Lock,
	LogIn,
	LogOut,
	MinusCircle,
	PlusCircle,
	RotateCcw,
	ScrollText,
	ShieldAlert,
	Terminal,
	Trash2,
	Upload,
	UserCog,
	UserPlus,
	XCircle,
} from "lucide-react";
import { useCallback, useState } from "react";

const ACTIVITY_TYPES = [
	{ key: "file_upload", label: "Загрузка" },
	{ key: "file_delete", label: "Удаление" },
	{ key: "work_order_check", label: "Проверка" },
	{ key: "synonym_add", label: "Синоним +" },
	{ key: "synonym_delete", label: "Синоним −" },
	{ key: "auth_login", label: "Вход" },
	{ key: "auth_login_failed", label: "Неудачный вход" },
	{ key: "auth_logout", label: "Выход" },
	{ key: "auth_account_locked", label: "Блокировка" },
	{ key: "auth_password_change", label: "Смена пароля" },
	{ key: "auth_password_reset", label: "Сброс пароля" },
	{ key: "auth_user_create", label: "Новый пользователь" },
	{ key: "auth_user_update", label: "Обновление" },
] as const;

const STATUS_OPTIONS = [
	{ key: "success", label: "Успех" },
	{ key: "error", label: "Ошибка" },
	{ key: "warning", label: "Предупреждение" },
	{ key: "in_progress", label: "В процессе" },
] as const;

const TIME_PRESETS = [
	{ key: "1h", label: "Час" },
	{ key: "today", label: "Сегодня" },
	{ key: "7d", label: "7 дней" },
] as const;

type ViewMode = "chronological" | "group-by-type" | "group-by-status";

const VIEW_MODES = [
	{ key: "chronological" as ViewMode, label: "Хронология" },
	{ key: "group-by-type" as ViewMode, label: "По типу" },
	{ key: "group-by-status" as ViewMode, label: "По статусу" },
] as const;

interface Filters {
	types: Set<string>;
	statuses: Set<string>;
	timePreset: string | null;
	sort: "desc" | "asc";
}

function floorToMinute(date: Date): Date {
	const d = new Date(date);
	d.setSeconds(0, 0);
	return d;
}

function getTimeRange(preset: string): { from: string; to?: string } {
	const now = new Date();
	switch (preset) {
		case "1h": {
			const from = floorToMinute(new Date(now.getTime() - 60 * 60 * 1000));
			return { from: from.toISOString() };
		}
		case "today": {
			const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
			return { from: from.toISOString() };
		}
		case "7d": {
			const from = floorToMinute(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
			return { from: from.toISOString() };
		}
		default:
			return { from: new Date(0).toISOString() };
	}
}

/** Кнопка-чип для фильтра */
function FilterChip({
	label,
	active,
	onClick,
}: {
	label: string;
	active: boolean;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
				active
					? "border-blue-300 bg-blue-50 text-blue-700"
					: "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
			}`}
		>
			{label}
		</button>
	);
}

/** Иконка по типу действия */
function ActivityIcon({ type }: { type: string }) {
	const cls = "h-5 w-5";
	switch (type) {
		case "file_upload":
			return <Upload className={`${cls} text-blue-500`} />;
		case "file_delete":
			return <Trash2 className={`${cls} text-red-500`} />;
		case "work_order_check":
			return <ClipboardCheck className={`${cls} text-amber-500`} />;
		case "synonym_add":
			return <PlusCircle className={`${cls} text-green-500`} />;
		case "synonym_delete":
			return <MinusCircle className={`${cls} text-orange-500`} />;
		case "auth_login":
			return <LogIn className={`${cls} text-green-600`} />;
		case "auth_login_failed":
			return <ShieldAlert className={`${cls} text-red-500`} />;
		case "auth_logout":
			return <LogOut className={`${cls} text-slate-500`} />;
		case "auth_account_locked":
			return <Lock className={`${cls} text-red-600`} />;
		case "auth_password_change":
		case "auth_password_reset":
			return <KeyRound className={`${cls} text-amber-600`} />;
		case "auth_user_create":
			return <UserPlus className={`${cls} text-blue-600`} />;
		case "auth_user_update":
			return <UserCog className={`${cls} text-violet-500`} />;
		default:
			return <ScrollText className={`${cls} text-slate-400`} />;
	}
}

/** Бейдж статуса */
function StatusBadge({ status }: { status: string }) {
	const tones: Record<string, "success" | "error" | "warn" | "info" | "neutral"> = {
		success: "success",
		error: "error",
		warning: "warn",
		in_progress: "info",
	};

	const labels: Record<string, string> = {
		success: "Успех",
		error: "Ошибка",
		warning: "Предупреждение",
		in_progress: "В процессе",
	};

	return (
		<Badge variant="pill" tone={tones[status] ?? "neutral"} className="gap-1">
			{status === "in_progress" && <Loader2 className="h-3 w-3 animate-spin" />}
			{status === "success" && <CheckCircle2 className="h-3 w-3" />}
			{status === "error" && <XCircle className="h-3 w-3" />}
			{status === "warning" && <AlertTriangle className="h-3 w-3" />}
			{labels[status] ?? status}
		</Badge>
	);
}

/** Бейдж уровня детали */
function DetailLevelBadge({ level }: { level: string }) {
	const tones: Record<string, "error" | "warn" | "info" | "neutral"> = {
		error: "error",
		warning: "warn",
		info: "info",
		debug: "neutral",
	};

	return (
		<Badge variant="tag" tone={tones[level] ?? "neutral"}>
			{level}
		</Badge>
	);
}

/** Склонение существительного по числу: pluralize(2, 'деталь', 'детали', 'деталей') → 'детали' */
function pluralize(n: number, one: string, few: string, many: string): string {
	const abs = Math.abs(n);
	const mod10 = abs % 10;
	const mod100 = abs % 100;
	if (mod10 === 1 && mod100 !== 11) return one;
	if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
	return many;
}

/** Форматирование длительности в человекочитаемый вид */
function formatDuration(ms: number): string {
	if (ms < 1000) return `${ms} мс`;
	const totalSec = Math.floor(ms / 1000);
	if (totalSec < 60) return `${(ms / 1000).toFixed(1)} с`;
	const minutes = Math.floor(totalSec / 60);
	const sec = totalSec % 60;
	if (minutes < 60) return sec > 0 ? `${minutes} мин ${sec} с` : `${minutes} мин`;
	const hours = Math.floor(minutes / 60);
	const min = minutes % 60;
	return min > 0 ? `${hours} ч ${min} мин` : `${hours} ч`;
}

/** Абсолютное время (чч:мм, с датой если не сегодня) */
function AbsoluteTime({ datetime }: { datetime: string }) {
	const date = new Date(`${datetime.replace(" ", "T")}Z`);
	const now = new Date();
	const isToday =
		date.getFullYear() === now.getFullYear() &&
		date.getMonth() === now.getMonth() &&
		date.getDate() === now.getDate();

	const time = date.toLocaleString("ru-RU", {
		hour: "2-digit",
		minute: "2-digit",
	});

	const full = date.toLocaleString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	});

	const display = isToday
		? time
		: date.toLocaleString("ru-RU", {
				day: "2-digit",
				month: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
			});

	return (
		<time dateTime={datetime} title={full} className="text-xs text-slate-400">
			{display}
		</time>
	);
}

const PINO_LEVEL_NAMES: Record<number, string> = {
	10: "TRACE",
	20: "DEBUG",
	30: "INFO",
	40: "WARN",
	50: "ERROR",
	60: "FATAL",
};

const PINO_LEVEL_COLORS: Record<number, string> = {
	10: "text-slate-400",
	20: "text-slate-500",
	30: "text-blue-600",
	40: "text-amber-600",
	50: "text-red-600",
	60: "text-red-800",
};

function formatPinoLine(entry: PinoLogEntry): {
	time: string;
	level: string;
	levelColor: string;
	msg: string;
	extra: string;
} {
	const date = new Date(entry.time);
	const hh = String(date.getHours()).padStart(2, "0");
	const mm = String(date.getMinutes()).padStart(2, "0");
	const ss = String(date.getSeconds()).padStart(2, "0");
	const ms = String(date.getMilliseconds()).padStart(3, "0");
	const time = `${hh}:${mm}:${ss}.${ms}`;

	const level = PINO_LEVEL_NAMES[entry.level] ?? String(entry.level);
	const levelColor = PINO_LEVEL_COLORS[entry.level] ?? "text-slate-500";

	const skipKeys = new Set(["time", "level", "msg", "pid", "hostname"]);
	const extras = Object.entries(entry)
		.filter(([k]) => !skipKeys.has(k))
		.map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`)
		.join(" ");

	return { time, level, levelColor, msg: entry.msg ?? "", extra: extras };
}

/** Просмотрщик Pino-логов */
function PinoLogViewer({ activityId }: { activityId: number }) {
	const { data, isLoading } = useQuery({
		queryKey: ["activity-logs", activityId],
		queryFn: () => getActivityLogs(activityId),
	});

	if (isLoading) {
		return (
			<div className="flex items-center gap-2 py-3">
				<Loader2 className="h-4 w-4 animate-spin text-slate-400" />
				<span className="text-xs text-slate-400">Загрузка логов...</span>
			</div>
		);
	}

	const logs = data?.logs ?? [];

	if (logs.length === 0) {
		return <p className="text-xs text-slate-400 py-2">Нет логов</p>;
	}

	return (
		<div className="max-h-80 overflow-auto rounded border border-slate-200 bg-slate-900 p-2">
			{logs.map((entry, i) => {
				const { time, level, levelColor, msg, extra } = formatPinoLine(entry);
				return (
					<div
						key={`${entry.time}-${i}`}
						className="font-mono text-[11px] leading-5 whitespace-pre-wrap"
					>
						<span className="text-slate-500">[{time}]</span>{" "}
						<span className={levelColor}>{level.padEnd(5)}</span>{" "}
						<span className="text-slate-200">{msg}</span>
						{extra && <span className="text-slate-500"> {extra}</span>}
					</div>
				);
			})}
			{data && data.total >= 500 && (
				<p className="mt-1 text-[11px] text-slate-500">
					... показано {data.total} из возможных записей (лимит)
				</p>
			)}
		</div>
	);
}

/** Строка детали с раскрываемым metadata */
function DetailRow({
	detail,
}: {
	detail: { id: number; level: string; message: string; metadata: Record<string, unknown> | null };
}) {
	const [open, setOpen] = useState(false);
	const meta = detail.metadata;
	const keyCount = meta ? Object.keys(meta).length : 0;

	return (
		<div>
			<div className="flex items-start gap-2">
				<DetailLevelBadge level={detail.level} />
				<p className="flex-1 text-xs text-slate-700">{detail.message}</p>
				{meta && keyCount > 0 && (
					<button
						type="button"
						onClick={() => setOpen((v) => !v)}
						className="flex-shrink-0 text-xs text-slate-400 hover:text-slate-600 transition-colors"
					>
						{keyCount} {pluralize(keyCount, "поле", "поля", "полей")}
						{open ? " ▴" : " ▾"}
					</button>
				)}
			</div>
			{open && meta && (
				<div className="mt-1 ml-12 rounded bg-slate-100 px-2.5 py-1.5 font-mono text-[11px] text-slate-600 space-y-0.5">
					{Object.entries(meta).map(([k, v]) => (
						<div key={k}>
							<span className="text-slate-400">{k}:</span>{" "}
							<span className="text-slate-700">
								{typeof v === "object" ? JSON.stringify(v) : String(v)}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

/** Панель деталей действия */
function DetailPanel({ activityId, total }: { activityId: number; total: number }) {
	const [showAll, setShowAll] = useState(false);
	const [showLogs, setShowLogs] = useState(false);
	const limit = showAll ? 100 : 20;

	const { data, isLoading } = useQuery({
		queryKey: ["activity-details", activityId, limit],
		queryFn: () => getActivityDetails(activityId, { limit }),
	});

	if (isLoading) {
		return (
			<div className="flex items-center gap-2 py-3 pl-10">
				<Loader2 className="h-4 w-4 animate-spin text-slate-400" />
				<span className="text-xs text-slate-400">Загрузка деталей...</span>
			</div>
		);
	}

	const details = data?.details ?? [];

	return (
		<div className="border-t border-slate-100 bg-slate-50 px-4 py-3 pl-10">
			<div className="mb-2 flex items-center gap-2">
				<button
					type="button"
					onClick={() => setShowLogs(false)}
					className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium transition-colors ${
						!showLogs ? "bg-slate-200 text-slate-800" : "text-slate-500 hover:text-slate-700"
					}`}
				>
					Детали
				</button>
				<button
					type="button"
					onClick={() => setShowLogs(true)}
					className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium transition-colors ${
						showLogs ? "bg-slate-200 text-slate-800" : "text-slate-500 hover:text-slate-700"
					}`}
				>
					<Terminal className="h-3 w-3" />
					Логи сервера
				</button>
			</div>

			{showLogs ? (
				<PinoLogViewer activityId={activityId} />
			) : details.length === 0 ? (
				<p className="text-xs text-slate-400">Нет деталей</p>
			) : (
				<div className="space-y-1.5">
					{details.map((detail) => (
						<DetailRow key={detail.id} detail={detail} />
					))}
					{!showAll && data?.hasMore && (
						<button
							type="button"
							onClick={() => setShowAll(true)}
							className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-800"
						>
							Показать все ({total})
						</button>
					)}
				</div>
			)}
		</div>
	);
}

/** Карточка действия с раскрывающимися деталями */
function ActivityCard({
	activity,
	expanded,
	onToggle,
}: {
	activity: Activity;
	expanded: boolean;
	onToggle: () => void;
}) {
	const hasDetails = activity.detailCount > 0;

	return (
		<div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
			<button
				type="button"
				onClick={hasDetails ? onToggle : undefined}
				className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${
					hasDetails ? "cursor-pointer hover:bg-slate-50" : ""
				}`}
			>
				<div className="mt-0.5 flex-shrink-0">
					{hasDetails ? (
						expanded ? (
							<ChevronDown className="h-5 w-5 text-slate-400" />
						) : (
							<ChevronRight className="h-5 w-5 text-slate-400" />
						)
					) : (
						<ActivityIcon type={activity.activityType} />
					)}
				</div>
				{hasDetails && (
					<div className="mt-0.5 flex-shrink-0">
						<ActivityIcon type={activity.activityType} />
					</div>
				)}
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<span className="flex-shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-400">
							#{activity.id}
						</span>
						<p className="text-sm font-medium text-slate-900 truncate">{activity.description}</p>
					</div>
					<div className="mt-1 flex flex-wrap items-center gap-2">
						<StatusBadge status={activity.status} />
						{activity.detailCount > 0 && (
							<span className="text-xs text-slate-400">
								{activity.detailCount}{" "}
								{pluralize(activity.detailCount, "деталь", "детали", "деталей")}
							</span>
						)}
						{activity.durationMs != null && (
							<span className="text-xs text-slate-400">{formatDuration(activity.durationMs)}</span>
						)}
					</div>
				</div>
				<div className="flex-shrink-0">
					<AbsoluteTime datetime={activity.createdAt} />
				</div>
			</button>
			{expanded && hasDetails && (
				<DetailPanel activityId={activity.id} total={activity.detailCount} />
			)}
		</div>
	);
}

/** Аккордеон группы действий */
function GroupAccordion({
	groupKey,
	label,
	count,
	filters,
}: {
	groupKey: string;
	label: string;
	count: number;
	filters: { type?: string; status?: string; from?: string; sort: "desc" | "asc" };
}) {
	const [expanded, setExpanded] = useState(false);
	const [expandedActivityId, setExpandedActivityId] = useState<number | null>(null);

	const { data, isLoading } = useQuery({
		queryKey: ["activities-group", groupKey, filters],
		queryFn: () =>
			getActivities({
				...filters,
				limit: 50,
			}),
		enabled: expanded,
	});

	return (
		<div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
			<button
				type="button"
				onClick={() => setExpanded((prev) => !prev)}
				className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
			>
				{expanded ? (
					<ChevronDown className="h-5 w-5 text-slate-400" />
				) : (
					<ChevronRight className="h-5 w-5 text-slate-400" />
				)}
				<span className="text-sm font-medium text-slate-900">{label}</span>
				<span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
					{count}
				</span>
			</button>
			{expanded && (
				<div className="border-t border-slate-100 bg-slate-50 p-2 space-y-1">
					{isLoading && (
						<div className="flex items-center gap-2 py-3 px-4">
							<Loader2 className="h-4 w-4 animate-spin text-slate-400" />
							<span className="text-xs text-slate-400">Загрузка...</span>
						</div>
					)}
					{data?.activities.map((activity) => (
						<ActivityCard
							key={activity.id}
							activity={activity}
							expanded={expandedActivityId === activity.id}
							onToggle={() =>
								setExpandedActivityId((prev) => (prev === activity.id ? null : activity.id))
							}
						/>
					))}
					{data?.activities.length === 0 && (
						<p className="px-4 py-2 text-xs text-slate-400">Нет действий</p>
					)}
				</div>
			)}
		</div>
	);
}

/** Группированный вид */
function GroupedView({
	groupBy,
	filters,
}: {
	groupBy: "type" | "status";
	filters: Filters;
}) {
	const queryParams = {
		groupBy,
		type: filters.types.size > 0 ? [...filters.types].join(",") : undefined,
		status: filters.statuses.size > 0 ? [...filters.statuses].join(",") : undefined,
		from: filters.timePreset ? getTimeRange(filters.timePreset).from : undefined,
	};

	const { data, isLoading } = useQuery({
		queryKey: ["activity-summary", queryParams],
		queryFn: () => getActivitySummary(queryParams),
	});

	const typeLabels: Record<string, string> = {
		file_upload: "Загрузка файла",
		file_delete: "Удаление файла",
		work_order_check: "Проверка наряда",
		synonym_add: "Добавление синонима",
		synonym_delete: "Удаление синонима",
		auth_login: "Вход в систему",
		auth_login_failed: "Неудачный вход",
		auth_logout: "Выход из системы",
		auth_account_locked: "Блокировка аккаунта",
		auth_password_change: "Смена пароля",
		auth_password_reset: "Сброс пароля",
		auth_user_create: "Создание пользователя",
		auth_user_update: "Обновление пользователя",
	};

	const statusLabels: Record<string, string> = {
		success: "Успех",
		error: "Ошибка",
		warning: "Предупреждение",
		in_progress: "В процессе",
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<Loader2 className="h-6 w-6 animate-spin text-blue-500" />
				<span className="ml-2 text-sm text-slate-500">Загрузка...</span>
			</div>
		);
	}

	const groups = data?.groups ?? [];

	if (groups.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-16 text-center">
				<ScrollText className="h-12 w-12 text-slate-300" />
				<p className="mt-3 text-sm font-medium text-slate-500">Нет данных для группировки</p>
			</div>
		);
	}

	return (
		<div className="space-y-2">
			{groups.map((group) => {
				const label =
					groupBy === "type"
						? (typeLabels[group.key] ?? group.key)
						: (statusLabels[group.key] ?? group.key);

				const groupFilter =
					groupBy === "type"
						? { ...queryParams, type: group.key, groupBy: undefined }
						: { ...queryParams, status: group.key, groupBy: undefined };

				return (
					<GroupAccordion
						key={group.key}
						groupKey={group.key}
						label={label}
						count={group.count}
						filters={{
							type: groupFilter.type,
							status: groupFilter.status,
							from: groupFilter.from,
							sort: filters.sort,
						}}
					/>
				);
			})}
		</div>
	);
}

const defaultFilters: Filters = {
	types: new Set(),
	statuses: new Set(),
	timePreset: null,
	sort: "desc",
};

function hasActiveFilters(filters: Filters): boolean {
	return filters.types.size > 0 || filters.statuses.size > 0 || filters.timePreset !== null;
}

export function ActivityLogPage() {
	const [viewMode, setViewMode] = useState<ViewMode>("chronological");
	const [filters, setFilters] = useState<Filters>(defaultFilters);
	const [cursor, setCursor] = useState<string | undefined>(undefined);
	const [allActivities, setAllActivities] = useState<Activity[]>([]);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [expandedId, setExpandedId] = useState<number | null>(null);

	const queryParams = {
		limit: 50,
		type: filters.types.size > 0 ? [...filters.types].join(",") : undefined,
		status: filters.statuses.size > 0 ? [...filters.statuses].join(",") : undefined,
		from: filters.timePreset ? getTimeRange(filters.timePreset).from : undefined,
		sort: filters.sort,
	};

	const { data, isLoading, error } = useQuery({
		queryKey: ["activities", queryParams],
		queryFn: () => getActivities(queryParams),
		refetchInterval: (query) => {
			const activities = query.state.data?.activities;
			if (activities?.some((a) => a.status === "in_progress")) return 5000;
			return false;
		},
	});

	const activities = allActivities.length > 0 ? allActivities : (data?.activities ?? []);
	const hasMore = allActivities.length > 0 ? !!cursor : (data?.hasMore ?? false);

	const resetPagination = useCallback(() => {
		setAllActivities([]);
		setCursor(undefined);
	}, []);

	const toggleType = (type: string) => {
		resetPagination();
		setFilters((prev) => {
			const next = new Set(prev.types);
			if (next.has(type)) next.delete(type);
			else next.add(type);
			return { ...prev, types: next };
		});
	};

	const toggleStatus = (status: string) => {
		resetPagination();
		setFilters((prev) => {
			const next = new Set(prev.statuses);
			if (next.has(status)) next.delete(status);
			else next.add(status);
			return { ...prev, statuses: next };
		});
	};

	const setTimePreset = (preset: string | null) => {
		resetPagination();
		setFilters((prev) => ({
			...prev,
			timePreset: prev.timePreset === preset ? null : preset,
		}));
	};

	const toggleSort = () => {
		resetPagination();
		setFilters((prev) => ({
			...prev,
			sort: prev.sort === "desc" ? "asc" : "desc",
		}));
	};

	const resetFilters = () => {
		resetPagination();
		setFilters(defaultFilters);
	};

	const handleLoadMore = async () => {
		const lastCursor = allActivities.length > 0 ? cursor : data?.nextCursor;
		if (!lastCursor) return;
		setIsLoadingMore(true);

		try {
			const more = await getActivities({ ...queryParams, cursor: lastCursor });
			const base = allActivities.length > 0 ? allActivities : (data?.activities ?? []);
			setAllActivities([...base, ...more.activities]);
			setCursor(more.nextCursor ?? undefined);
		} finally {
			setIsLoadingMore(false);
		}
	};

	const toggleExpand = (id: number) => {
		setExpandedId((prev) => (prev === id ? null : id));
	};

	return (
		<div className="mx-auto max-w-4xl p-6">
			<PageHeader
				title="Логи"
				subtitle={
					<>
						Хронологическая лента действий в системе
						{data?.total != null && ` (${data.total})`}
					</>
				}
				actions={
					<>
						<div className="flex rounded-md border border-slate-200 bg-white p-0.5">
							{VIEW_MODES.map((mode) => (
								<button
									key={mode.key}
									type="button"
									onClick={() => setViewMode(mode.key)}
									className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
										viewMode === mode.key
											? "bg-slate-100 text-slate-900"
											: "text-slate-500 hover:text-slate-700"
									}`}
								>
									{mode.label}
								</button>
							))}
						</div>
						{viewMode === "chronological" && (
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={toggleSort}
								title={filters.sort === "desc" ? "Сначала новые" : "Сначала старые"}
							>
								<ArrowDownUp className="h-3.5 w-3.5" />
								{filters.sort === "desc" ? "Новые" : "Старые"}
							</Button>
						)}
					</>
				}
			/>

			{/* Фильтры */}
			<div className="mb-4 space-y-2">
				<div className="flex flex-wrap items-center gap-1.5">
					<span className="text-xs font-medium text-slate-400 mr-1">Тип:</span>
					{ACTIVITY_TYPES.map((t) => (
						<FilterChip
							key={t.key}
							label={t.label}
							active={filters.types.has(t.key)}
							onClick={() => toggleType(t.key)}
						/>
					))}
				</div>
				<div className="flex flex-wrap items-center gap-1.5">
					<span className="text-xs font-medium text-slate-400 mr-1">Статус:</span>
					{STATUS_OPTIONS.map((s) => (
						<FilterChip
							key={s.key}
							label={s.label}
							active={filters.statuses.has(s.key)}
							onClick={() => toggleStatus(s.key)}
						/>
					))}
				</div>
				<div className="flex flex-wrap items-center gap-1.5">
					<span className="text-xs font-medium text-slate-400 mr-1">Период:</span>
					{TIME_PRESETS.map((t) => (
						<FilterChip
							key={t.key}
							label={t.label}
							active={filters.timePreset === t.key}
							onClick={() => setTimePreset(t.key)}
						/>
					))}
					{hasActiveFilters(filters) && (
						<button
							type="button"
							onClick={resetFilters}
							className="ml-2 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
						>
							<RotateCcw className="h-3 w-3" />
							Сбросить
						</button>
					)}
				</div>
			</div>

			{viewMode !== "chronological" ? (
				<GroupedView groupBy={viewMode === "group-by-type" ? "type" : "status"} filters={filters} />
			) : (
				<>
					{isLoading && (
						<div className="flex items-center justify-center py-12">
							<Loader2 className="h-6 w-6 animate-spin text-blue-500" />
							<span className="ml-2 text-sm text-slate-500">Загрузка...</span>
						</div>
					)}

					{error && (
						<div className="rounded-lg border border-red-200 bg-red-50 p-4">
							<p className="text-sm text-red-700">Ошибка загрузки: {(error as Error).message}</p>
						</div>
					)}

					{!isLoading && !error && activities.length === 0 && (
						<div className="flex flex-col items-center justify-center py-16 text-center">
							<ScrollText className="h-12 w-12 text-slate-300" />
							<p className="mt-3 text-sm font-medium text-slate-500">
								{hasActiveFilters(filters)
									? "Нет действий по заданным фильтрам"
									: "Действия пока не зафиксированы"}
							</p>
							<p className="mt-1 text-xs text-slate-400">
								{hasActiveFilters(filters)
									? "Попробуйте изменить параметры фильтрации"
									: "Загрузите файл или проведите проверку — действия появятся здесь"}
							</p>
						</div>
					)}

					{activities.length > 0 && (
						<div className="space-y-2">
							{activities.map((activity) => (
								<ActivityCard
									key={activity.id}
									activity={activity}
									expanded={expandedId === activity.id}
									onToggle={() => toggleExpand(activity.id)}
								/>
							))}

							{hasMore && (
								<div className="pt-2 text-center">
									<button
										type="button"
										onClick={handleLoadMore}
										disabled={isLoadingMore}
										className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
									>
										{isLoadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
										Показать ещё
									</button>
								</div>
							)}
						</div>
					)}
				</>
			)}
		</div>
	);
}
